import { useContext, useEffect, useMemo, useState } from "react";
import { Order, OrderItem, OrderStatus } from "@/types";
import { useAppDispatch, useAppSelector, useResponsive } from "@/hooks";
import { CircularProgress, Stack } from "@mui/material";
import useRoutes from "@/hooks/useRoutes";
import { useParams, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase";
import TimeoutDialog from "./components/TimeoutDialog";
import OrderCanceledDialog from "./components/OrderCanceledDialog";
import OrderStatusDesktop from "./components/OrderStatusDesktop";
import OrderStatusMobile from "./components/OrderStatusMobile";
import { deleteOrder } from "@/features/order/slice";
import { useGetOrderByIdQuery } from "@/features/organizations/api/ordersApi";
import { getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";
import { WebsiteContext } from "../website/WebsiteProvider";

const ALLOWED_STATUSES: OrderStatus[] = [
  OrderStatus.REVIEWING,
  OrderStatus.PENDING_PAYMENT,
  OrderStatus.PREPARING,
  OrderStatus.SENDING,
  OrderStatus.WAITING,
];

enum ActiveView {
  Thanks = "thanks",
  Status = "status",
}

const OrderStatusPage = () => {
  const [navigate] = useRoutes();
  const dispatch = useAppDispatch();
  const activeOrgId = useAppSelector((state) => state.ui.activeOrganization);
  const { orderId, organizationAlias } = useParams();
  const location = useLocation();
  const [activeOrder, setActiveOrder] = useState<Order | null | undefined>();
  const [activeView, setActiveView] = useState<ActiveView | null>(null);

  const { organization, branch } = useContext(WebsiteContext);
  const organizationId = organization?.id || activeOrgId;
  const { isDesktop } = useResponsive();

  const { data: fetchedOrder, isLoading: isFetchingOrder } = useGetOrderByIdQuery(
    { organizationId: organizationId as string, orderId: orderId as string },
    {
      skip:
        Boolean(activeOrder) ||
        activeView !== ActiveView.Thanks ||
        !organizationId ||
        !orderId,
    }
  );

  const order = (activeOrder || fetchedOrder) as Order | undefined;
  const isCompleted =
    activeView === ActiveView.Thanks ||
    order?.status === OrderStatus.COMPLETED ||
    (order?.status as string) === OrderStatus.DELIVERED;

  const subtotalValue = useMemo(
    () => order?.subtotal ?? getOrderSubtotal(order?.items ?? []),
    [order]
  );

  const deliveryService = organization?.settings?.services?.delivery;

  const deliveryValue = useMemo(() => {
    if (!order) return null;
    if (order.flow !== "delivery") return null;

    if (order.deliveryFee !== undefined && order.deliveryFee !== null) {
      return order.deliveryFee;
    }

    if (deliveryService?.type === "manual") {
      return "A convenir";
    }

    if (
      deliveryService?.type === "fixed" &&
      deliveryService.fee?.value !== undefined &&
      deliveryService.fee?.value !== null
    ) {
      return Number(deliveryService.fee.value);
    }

    return null;
  }, [order, deliveryService]);

  const taxesValue = useMemo(() => {
    if (!organization?.settings?.includeTax) return null;
    if (!order) return null;
    return order.taxes ?? getOrderTaxes(order.items as OrderItem[]);
  }, [organization?.settings?.includeTax, order]);

  const deliveryFee =
    typeof order?.deliveryFee === "number"
      ? order.deliveryFee
      : typeof order?.serviceFee === "number"
        ? order.serviceFee
        : typeof deliveryValue === "number"
          ? deliveryValue
          : 0;

  const totalValue = getOrderTotal(
    order?.items as OrderItem[],
    organization?.settings?.includeTax,
    Number(deliveryFee)
  );

  const handleGoBack = () => {
    dispatch(deleteOrder({}));

    if (organizationAlias) {
      navigate("organizations.menu.main");
    } else {
      navigate("menus");
    }
  };

  const handleChat = () => {
    const selectedBranch = branch?.find((b) => b.id === order?.branch?.id);
    const whatsapp = selectedBranch?.whatsapp || organization?.whatsapp;

    if (!whatsapp) {
      console.error("No hay WhatsApp disponible para esta orden");
      return;
    }

    const message = `Hola, necesito ayuda con mi pedido N° ${order?.number ?? ""}`;
    const text = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsapp}?text=${text}`, "_blank");
  };

  useEffect(() => {
    if (!organizationId || !orderId) return;

    if (location.state?.order && location.state.order.id === orderId) {
      const navOrder = location.state.order;
      setActiveOrder(navOrder);
      const navStatus = navOrder.status as OrderStatus;
      if (ALLOWED_STATUSES.includes(navStatus)) {
        setActiveView(ActiveView.Status);
      } else if (
        navStatus === OrderStatus.COMPLETED ||
        (navStatus as string) === OrderStatus.DELIVERED ||
        location.state.view === "thanks"
      ) {
        setActiveView(ActiveView.Thanks);
      }
    } else {
      setActiveOrder(undefined);
      setActiveView(null);
    }

    const colRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);

    const unsubscribe = onSnapshot(
      colRef,
      (snapshot) => {
        const data = snapshot.data();

        if (data) {
          const docOrder: Order = { ...data, id: snapshot.id } as Order;
          setActiveOrder(docOrder);

          const currentStatus = docOrder.status as OrderStatus;
          if (ALLOWED_STATUSES.includes(currentStatus)) {
            setActiveView(ActiveView.Status);
          } else if (
            currentStatus === OrderStatus.COMPLETED ||
            (currentStatus as string) === OrderStatus.DELIVERED
          ) {
            setActiveView(ActiveView.Thanks);
          } else {
            handleGoBack();
          }
        } else {
          if (!location.state?.order || location.state.order.id !== orderId) {
            setActiveOrder(undefined);
          }
          setActiveView(ActiveView.Thanks);
        }
      },
      (error) => {
        console.error(error);
      }
    );

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, orderId, db, location.state]);

  if (!activeView || (isFetchingOrder && !order) || !order) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" px={3} py={6} minHeight="100vh">
        <CircularProgress />
      </Stack>
    );
  }

  const sharedViewProps = {
    order,
    orderNumber: order.number,
    isCompleted,
    organizationId,
    organizationAlias,
    orderId,
    surveyId: organization?.settings?.postOrderSurveyId,
    currency: ((organization?.settings as any)?.currency || "NIO") as any,
    subtotalValue,
    deliveryValue,
    taxesValue,
    totalValue,
    onGoBack: handleGoBack,
    onChat: handleChat,
  };

  return (
    <>
      {isDesktop ? (
        <OrderStatusDesktop {...sharedViewProps} />
      ) : (
        <OrderStatusMobile {...sharedViewProps} />
      )}

      {!isCompleted && order?.expiresAt && (
        <>
          <TimeoutDialog order={order} onGoBack={handleGoBack} />
          <OrderCanceledDialog order={order} onGoBack={handleGoBack} />
        </>
      )}
    </>
  );
};

export default OrderStatusPage;
